import axios from 'axios'

const API_BASE_URL = import.meta.env.VITE_API_URL || 'https://ai-trading-journal-api.dnash29.workers.dev/api'

class ApiService {
  constructor() {
    this.client = axios.create({
      baseURL: API_BASE_URL,
      timeout: 30000,
      headers: {
        'Content-Type': 'application/json',
      },
    })

    // Request interceptor
    this.client.interceptors.request.use(
      (config) => {
        const token = localStorage.getItem('token')
        if (token) {
          config.headers.Authorization = `Bearer ${token}`
        }
        return config
      },
      (error) => {
        return Promise.reject(error)
      }
    )

    // Response interceptor
    this.client.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error.response?.status === 401) {
          localStorage.removeItem('token')
          // Only redirect if we're not already on login/register pages
          if (!window.location.pathname.includes('/login') && !window.location.pathname.includes('/register')) {
            window.location.href = '/login'
          }
        }
        return Promise.reject(error)
      }
    )
  }

  setAuthToken(token) {
    if (token) {
      this.client.defaults.headers.common['Authorization'] = `Bearer ${token}`
    } else {
      delete this.client.defaults.headers.common['Authorization']
    }
  }

  // Generic HTTP methods
  get(url, config = {}) {
    return this.client.get(url, config)
  }

  post(url, data = {}, config = {}) {
    return this.client.post(url, data, config)
  }

  put(url, data = {}, config = {}) {
    return this.client.put(url, data, config)
  }

  delete(url, config = {}) {
    return this.client.delete(url, config)
  }

  // Auth endpoints
  auth = {
    login: (credentials) => this.post('/auth/login', credentials),
    register: (userData) => this.post('/auth/register', userData),
    me: () => this.get('/auth/me'),
  }

  // Trades endpoints
  trades = {
    getAll: (params = {}) => this.get('/trades', { params }),
    getById: (id) => this.get(`/trades/${id}`),
    create: (tradeData) => this.post('/trades', tradeData),
    update: (id, tradeData) => this.put(`/trades/${id}`, tradeData),
    delete: (id) => this.delete(`/trades/${id}`),
    import: (tradesData) => this.post('/trades/import', tradesData),
  }

  // AI endpoints
  ai = {
    getInsights: (params = {}) => this.get('/ai/insights', { params }),
    analyzeTrade: (tradeId) => this.post(`/ai/analyze-trade/${tradeId}`),
    getEmotionalAnalysis: (params = {}) => this.get('/ai/emotional-analysis', { params }),
    getAlerts: (params = {}) => this.get('/ai/alerts', { params }),
    markAlertRead: (alertId) => this.put(`/ai/alerts/${alertId}/read`),
    dismissAlert: (alertId) => this.put(`/ai/alerts/${alertId}/dismiss`),
    getPatterns: (params = {}) => this.get('/ai/patterns', { params }),
    getCoaching: (data) => this.post('/ai/coaching', data),
  }

  // Analytics endpoints
  analytics = {
    getOverview: (params = {}) => this.get('/analytics/overview', { params }),
    getPerformance: (params = {}) => this.get('/analytics/performance', { params }),
    getRiskMetrics: (params = {}) => this.get('/analytics/risk', { params }),
    getTimeAnalysis: (params = {}) => this.get('/analytics/time-analysis', { params }),
    getSymbolAnalysis: (params = {}) => this.get('/analytics/symbols', { params }),
  }

  // Brokers endpoints
  brokers = {
    getConnections: () => this.get('/brokers/connections'),
    connect: (brokerData) => this.post('/brokers/connect', brokerData),
    disconnect: (connectionId) => this.delete(`/brokers/connections/${connectionId}`),
    sync: (connectionId) => this.post(`/brokers/sync/${connectionId}`),
    getSupportedBrokers: () => this.get('/brokers/supported'),
  }

  // Social endpoints
  social = {
    getSharedTrades: (params = {}) => this.get('/social/trades', { params }),
    shareTrade: (tradeId, shareData) => this.post(`/social/share/${tradeId}`, shareData),
    unshareTrade: (shareId) => this.delete(`/social/shares/${shareId}`),
    likeTrade: (shareId) => this.post(`/social/shares/${shareId}/like`),
    unlikeTrade: (shareId) => this.delete(`/social/shares/${shareId}/like`),
    getLeaderboard: (params = {}) => this.get('/social/leaderboard', { params }),
  }
}

export const api = new ApiService()
export default api